<script>
import BaseIcon from '@/rex-components/BaseIcon.vue';
import RexSearchSelect from '@/rex-components/RexSearchSelect.vue';
import RexButton from '@/rex-components/rex-button.vue';
import Card from '@/rex-components/rex-card.vue';
import RdsCreateEditForm from './FormComponents/RdsCreateEditForm.vue';
import LambdaCreateEditForm from './FormComponents/LambdaCreateEditForm.vue';
import ElastiCacheCreateEditForm from './FormComponents/ElastiCacheCreateEditForm.vue';
import IngressCreateEditForm from './FormComponents/IngressCreateEditForm.vue';
import { mapActions, mapMutations, mapState } from 'vuex';
import { notificationError, notificationSucess } from '@/utils/notification';
import {
  AUTO_INSTALL_ALL,
  AWS_SERVICE_DISCOVERY,
  FILTER_SERVICES_ON_INSTALLATION_TYPE_CHANGE,
  GET_INSTALLED_SERVICES,
  SERVICE_LEVEL_AUTO_INSTALL,
  TOGGLE_MONITORING,
} from '@/store/modules/cloud/aws/aws.constants';
import {
  installationTypes,
  allServices,
  configureableEngines,
  SLUG_NAME,
} from '@/components/Cloud/services/aws/aws.services.types';
import RexRow from '@/rex-components/RexRow.vue';
import RexColumn from '@/rex-components/RexColumn.vue';
import RexSwitchV3 from '@/rex-components/RexSwitchV3.vue';
import { UPDATE_SEARCH_SERVICE } from '@/store/modules/cloud/connections/services.constants';
import { CloudService } from '@/services/api/integration.service';

export default {
  name: 'AwsServicesComponent',
  methods: {
    ...mapActions('cloudState/aws', [
      GET_INSTALLED_SERVICES,
      SERVICE_LEVEL_AUTO_INSTALL,
      TOGGLE_MONITORING,
      AUTO_INSTALL_ALL,
      'getAvailableServices',
    ]),
    ...mapMutations('cloudState/aws', [
      FILTER_SERVICES_ON_INSTALLATION_TYPE_CHANGE,
    ]),
    ...mapMutations('cloudState/connections', [UPDATE_SEARCH_SERVICE]),
    handleAllMonitorToggle() {
      this.isMonitoringEnabledForAll = !this.isMonitoringEnabledForAll;
    },
    onInstallationTypeChange(item) {
      this.installationType = item.name;
      if (item.id === 'All') {
        this.availableServices = this.allServices.filter(
          (service) => !this.installedSlugs.includes(service.slug),
        );
      }
      if (item.id === 'Manual') {
        this.availableServices = this.allServices.filter(
          (service) => !this.installedSlugs.includes(service.slug),
        );
      }
      if (item.id === 'Auto') {
        this.availableServices = this.allServices.filter(
          (service) =>
            !this.installedSlugs.includes(service.slug) &&
            service.showAutoInstall,
        );
      }
    },
    async handleServiceLevelAutoInstall(serviceSlug) {
      const subscriptionId = this.getSubscriptionId;
      const engines = {};
      if (serviceSlug === 'rds') {
        this.configureableEngines.forEach((engine) => {
          engines[engine] = true;
        });
      }
      const data = await this[SERVICE_LEVEL_AUTO_INSTALL]({
        serviceSlug,
        subscriptionId,
        meta: {
          ...(serviceSlug === 'rds' ? { configuredEngines: engines } : {}),
        },
      });
      if (!data.success) return notificationError(data.message);
      this.installedSlugs.push(serviceSlug);
      this.allServices = this.allServices.map((service) => {
        if (service.slug === serviceSlug) {
          return {
            ...service,
            isMonitoringEnabled: true,
          };
        }
        return { ...service };
      });
      notificationSucess(
        this.$t(`aws.aws${serviceSlug}ConfiguredSuccessfully`),
      );
      this.updateServicesPostToggleChange();
    },
    async handleMonitorToggeling(serviceSlug, toggleState) {
      const subscriptionId = this.getSubscriptionId;
      const data = await this[TOGGLE_MONITORING]({
        subscriptionId,
        serviceSlug,
        toggleState,
      });
      if (!data.success) return;
      if (serviceSlug === 'all') {
        this.allServices = this.allServices.map((service) => ({
          ...service,
          isMonitoringEnabled: toggleState,
        }));
        notificationSucess(
          toggleState
            ? this.$t(`aws.enabledMonitoringForAllServices`)
            : this.$t(`aws.disabledMonitoringForAllServices`),
        );
      } else {
        this.allServices = this.allServices.map((service) => {
          if (service.slug === serviceSlug) {
            return {
              ...service,
              isMonitoringEnabled: toggleState,
            };
          }
          return { ...service };
        });
        notificationSucess(
          toggleState
            ? this.$t(`aws.enabledMonitoringFor${serviceSlug}`)
            : this.$t(`aws.disabledMonitoringFor${serviceSlug}`),
        );
      }
      this.updateServicesPostToggleChange();
    },
    closeFormCards(canecelType) {
      if (canecelType === 'edit') {
        this.editConfigurationServiceSlug = '';
      }

      if (canecelType === 'manualInstall') {
        this.manualInstallServiceSlug = '';
      }
    },
    async fetchInstalledServices() {
      const subscriptionId = this.getSubscriptionId;
      try {
        const data = await this[GET_INSTALLED_SERVICES]({ subscriptionId });
        this.installedSlugs = data.map((service) => service.slug);
        this.allServices = this.allServices.map((service) => {
          const serviceFromAPi = data?.find(
            (dataService) => dataService?.slug === service?.slug,
          );
          if (!serviceFromAPi) {
            return service;
          }
          return {
            ...service,
            isMonitoringEnabled:
              serviceFromAPi?.isActive === 1 || service.isMonitoringEnabled,
            dataCapturingStatus: serviceFromAPi?.dataCapturingStatus,
          };
        });

        this.updateServicesPostToggleChange();
      } catch (error) {
        console.error('Failed to fetch installed services:', error);
      }
    },
    updateServicesPostToggleChange() {
      this.installedServices = this.allServices.filter((service) =>
        this.installedSlugs.includes(service.slug),
      );
      this.availableServices = this.allServices.filter(
        (service) => !this.installedSlugs.includes(service.slug),
      );

      this.isMonitoringEnabledForAll =
        this.installedServices.length === 0
          ? false
          : this.installedServices.every(
              (service) => service.isMonitoringEnabled,
            );
    },
    updateAllServices(serviceSlug, toggleState) {
      if (serviceSlug === 'all') {
        this.allServices = this.allServices.map((service) => ({
          ...service,
          isMonitoringEnabled: toggleState,
        }));
      } else {
        this.allServices = this.allServices.map((service) => {
          if (service.slug === serviceSlug) {
            return {
              ...service,
              isMonitoringEnabled: toggleState,
            };
          }
          return { ...service };
        });
      }
      this.installedSlugs.push(serviceSlug);

      this.updateServicesPostToggleChange();

      this.editConfigurationServiceSlug = '';
      this.manualInstallServiceSlug = '';
    },
    async handleAutoInstallAll() {
      const engines = {};
      this.configureableEngines.forEach((engine) => {
        engines[engine] = true;
      });
      let meta = {};
      const autoInstallationServices = this.availableServices
        .filter((service) => service.showAutoInstall)
        .map((service) => {
          if (service.slug === 'rds') {
            meta = {
              configuredEngines: engines,
            };
          }
          return {
            serviceSlug: service.slug,
            subscriptionId: this.getSubscriptionId,
            meta,
          };
        });
      if (autoInstallationServices.length < 1) return;
      try {
        const { data } = await this[AUTO_INSTALL_ALL]({
          services: autoInstallationServices,
        });
        this.allServices = this.allServices.map((service) => {
          if (service.showAutoInstall) {
            return {
              ...service,
              isMonitoringEnabled: true,
            };
          }
          return { ...service };
        });

        this.installedSlugs = [
          ...this.installedSlugs,
          ...autoInstallationServices.map((service) => service.serviceSlug),
        ];

        this.installedServices = this.allServices.filter(
          (service) => service.showAutoInstall,
        );
        this.availableServices = this.allServices.filter(
          (service) =>
            !service.showAutoInstall &&
            !this.installedSlugs.includes(service.slug),
        );
        this.isMonitoringEnabledForAll =
          this.installedServices.length === 0
            ? false
            : this.installedServices.every(
                (service) => service.isMonitoringEnabled,
              );
        const serviceNames = autoInstallationServices
          .map((service) => SLUG_NAME[service.serviceSlug.toUpperCase()])
          .join(', ');
        notificationSucess(
          this.$t('aws.configuredSuccessfully', { services: serviceNames }),
        );
      } catch (error) {
        console.log(error);
      }
    },
    getInstallButtonsDisableState(service) {
      return !this.serviceDiscovery.includes(service.slug.toUpperCase());
    },

    async fetchServiceDiscovery() {
      const subscriptionId = this.getSubscriptionId;
      const { data } = await CloudService.getAvailableServices({
        subscriptionId,
      });
      this.serviceDiscovery = data.data;
    },
  },
  computed: {
    ...mapState('cloudState/connections', ['searchedService']),
    getAvailableServices() {
      return this.allServices.filter(
        (service) =>
          !this.installedServices.some(
            (installed) => installed.slug === service.slug,
          ),
      );
    },
    getFilteredInstalledService() {
      return this.installedServices.filter((service) => {
        return service.title
          .toLowerCase()
          .includes(this.searchedService.toLowerCase());
      });
    },
    getAllServicesButtonStatus() {
      const autoInstallableSlugs = this.allServices
        .filter((service) => service.showAutoInstall)
        .map((service) => service.slug.toUpperCase());
      return this.installationType === 'Auto' &&
        autoInstallableSlugs.every((element) =>
          this.serviceDiscovery.includes(element),
        )
        ? 'transparent'
        : 'disabled';
    },
    getSubscriptionId() {
      return this.$route.params.subscriptionId;
    },
  },
  data() {
    return {
      expandInstalled: true,
      expandAvailable: true,
      editConfigurationServiceSlug: '',
      manualInstallServiceSlug: '',
      installationType: '',
      installationTypes: installationTypes,
      installedServices: [],
      availableServices: [],
      installedSlugs: [],
      isMonitoringEnabledForAll: false,
      allServices: allServices,
      configureableEngines,
      serviceDiscovery: [],
      serviceIntervalId: null,
      discoveryIntervalId: null,
    };
  },
  components: {
    BaseIcon,
    Card,
    RexButton,
    RexSearchSelect,
    RdsCreateEditForm,
    LambdaCreateEditForm,
    ElastiCacheCreateEditForm,
    IngressCreateEditForm,
    RexRow,
    RexColumn,
    RexSwitchV3,
  },
  async mounted() {
    // Fetch installed services data initially and then refresh every 60 seconds
    await this.fetchInstalledServices();
    this.serviceIntervalId = setInterval(() => {
      this.fetchInstalledServices();
    }, 60000);

    this[UPDATE_SEARCH_SERVICE]('');

    // Fetch service discovery data initially and then refresh every 60 seconds
    await this.fetchServiceDiscovery();
    this.discoveryIntervalId = setInterval(() => {
      this.fetchServiceDiscovery();
    }, 60000);
  },
  beforeUnmount() {
    // Clear the intervals when the component is unmounted
    clearInterval(this.serviceIntervalId);
    clearInterval(this.discoveryIntervalId);
  },
};
</script>

<template>
  <div class="p-4">
    <!-- installed section start -->
    <div class="installed-section">
      <!-- installed section header starts -->
      <div class="flex-between">
        <div>
          <BaseIcon :name="!expandInstalled ? `chevronUp` : `chevronDown`" />
          <button
            @click="expandInstalled = !expandInstalled"
            class="unstyled-button collapse-button"
          >
            {{ $t('aws.servicesPage.installed') }}
          </button>
        </div>

        <div class="flex-center">
          {{ $t('aws.servicesPage.monitoringForAll') }}

          <RexSwitchV3
            @onChange="(e) => handleMonitorToggeling('all', e)"
            :disabled="installedServices.length < 1"
            :checked="isMonitoringEnabledForAll"
          />
        </div>
      </div>
      <!-- installed section header ends -->

      <div v-if="expandInstalled">
        <!-- installed section body starts -->

        <!-- no installed services section starts -->
        <div
          class="no-installed-services-container"
          v-if="installedServices.length === 0"
        >
          <BaseIcon name="noConfigurations" />
          <h4 class="no-install-text">
            {{ $t('aws.servicesPage.noInstalledServices') }}
          </h4>
          <p class="no-install-text-description">
            {{ $t('aws.servicesPage.noInstalledServicesDescription') }}
          </p>
        </div>
        <!-- no installed services section ends -->

        <!-- container of all the cards component and the form component starts -->
        <RexRow :gutter="10">
          <component
            class="cards-container"
            :span="8"
            :is="editConfigurationServiceSlug ? 'RexColumn' : 'RexRow'"
            :gutter="[10, 10]"
          >
            <RexColumn
              style="margin-top: 10px"
              :span="editConfigurationServiceSlug ? 24 : 8"
              v-for="service in getFilteredInstalledService"
              :key="service.id"
            >
              <Card
                :custom-style="{
                  borderColor:
                    editConfigurationServiceSlug === service.slug
                      ? '#F59600'
                      : '',
                }"
              >
                <template #card-body>
                  <div class="d-flex cards-body">
                    <img
                      :src="service.imgSrc"
                      style="height: 45px; width: 45px; margin-right: 10px"
                    />
                    <div class="justify-content-between">
                      <div class="flex-between">
                        <div class="flex-center">
                          <p class="card-title">
                            {{ service.title }}
                          </p>
                          <div
                            :class="[
                              'instance-heartbeat ',
                              service.dataCapturingStatus ? 'green' : 'red',
                            ]"
                          ></div>
                        </div>

                        <RexSwitchV3
                          @onChange="
                            (e) => handleMonitorToggeling(service.slug, e)
                          "
                          :checked="service.isMonitoringEnabled"
                        />
                      </div>

                      <p class="card-descritpion">
                        {{ service.description }}
                      </p>
                    </div>
                  </div>
                </template>
                <template #card-footer>
                  <div
                    class="d-flex justify-content-end"
                    style="border-top: 0.0125rem solid grey"
                  >
                    <RexButton
                      @onClick="editConfigurationServiceSlug = service.slug"
                      id="edit-configiration"
                      :styling="{
                        border: '0px',
                        width: 'fit-content',
                        color: 'rgb(245, 150, 0)',
                        fontWeight: 'bold',
                      }"
                      variant="tertiary"
                    >
                      {{ $t('aws.servicesPage.editConfiguration') }}
                    </RexButton>
                  </div>
                </template>
              </Card>
            </RexColumn>
          </component>
          <RexColumn :span="editConfigurationServiceSlug ? 16 : 0">
            <RdsCreateEditForm
              @update-all-services="updateAllServices"
              @close="closeFormCards"
              type="edit"
              v-if="editConfigurationServiceSlug === 'rds'"
            />
            <LambdaCreateEditForm
              @update-all-services="updateAllServices"
              @close="closeFormCards"
              type="edit"
              v-if="editConfigurationServiceSlug === 'lambda'"
            />
            <ElastiCacheCreateEditForm
              @update-all-services="updateAllServices"
              @close="closeFormCards"
              type="edit"
              v-if="editConfigurationServiceSlug === 'elasticache'"
            />
            <IngressCreateEditForm
              @update-all-services="updateAllServices"
              @close="closeFormCards"
              type="edit"
              v-if="editConfigurationServiceSlug === 'elb'"
            />
          </RexColumn>
        </RexRow>
        <!-- container of all the cards component and the form component ends -->
      </div>
      <!-- installed section body ends -->
    </div>
    <!-- installed section ends -->

    <!-- available section start -->
    <div class="available-section">
      <!-- available section header starts -->
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <BaseIcon :name="!expandAvailable ? `chevronUp` : `chevronDown`" />
          <button
            @click="expandAvailable = !expandAvailable"
            class="unstyled-button collapse-button"
          >
            {{ $t('aws.servicesPage.available') }}
          </button>
        </div>

        <div>
          <div class="d-flex">
            <RexSearchSelect
              :current="installationType"
              :data="installationTypes"
              id="select-installation-type"
              @onChoose="onInstallationTypeChange"
              :heading="$t('aws.allServicesScreen.installationType')"
              width="14rem"
            />
            <RexButton
              @onClick="handleAutoInstallAll"
              id="auto-install"
              :styling="{ marginLeft: '10px', width: 'fit-content' }"
              :variant="getAllServicesButtonStatus"
            >
              {{ $t('aws.servicesPage.autoInstall') }}
            </RexButton>
          </div>
        </div>
      </div>
      <!-- available section header ends -->

      <!-- available section body starts -->
      <div v-if="expandAvailable">
        <!-- container of all the cards component and the form component starts -->
        <RexRow gutter="10">
          <component
            class="cards-container"
            :span="8"
            :is="manualInstallServiceSlug ? 'RexColumn' : 'RexRow'"
            :gutter="[10, 10]"
          >
            <RexColumn
              style="margin-top: 10px"
              :span="manualInstallServiceSlug ? 24 : 8"
              v-for="service in availableServices"
              :key="service.id"
            >
              <Card
                :custom-style="{
                  borderColor:
                    manualInstallServiceSlug === service.slug ? '#F59600' : '',
                }"
              >
                <template #card-body>
                  <div class="d-flex cards-body">
                    <img
                      :src="service.imgSrc"
                      style="height: 45px; width: 45px; margin-right: 10px"
                    />
                    <div class="justify-content-between">
                      <p class="card-title">
                        {{ service.title }}
                      </p>
                      <p class="card-descritpion">
                        {{ service.description }}
                      </p>
                    </div>
                  </div>
                </template>
                <template #card-footer>
                  <div
                    class="flex-between p-4"
                    style="border-top: 0.0125rem solid grey"
                  >
                    <button
                      @click="manualInstallServiceSlug = service.slug"
                      class="unstyled-button"
                      :disabled="getInstallButtonsDisableState(service)"
                    >
                      {{ $t('aws.servicesPage.mannualInstall') }}
                    </button>
                    <RexButton
                      v-if="service.showAutoInstall"
                      @onClick="handleServiceLevelAutoInstall(service.slug)"
                      :disabled="getInstallButtonsDisableState(service)"
                      :id="'auto-install-' + service.slug"
                      :styling="{
                        border: '0px',
                        width: 'fit-content',
                        color: 'rgb(245, 150, 0)',
                        fontWeight: 'bold',
                        cursor: getInstallButtonsDisableState(service)
                          ? 'not-allowed'
                          : 'pointer',
                      }"
                      variant="tertiary"
                    >
                      {{ $t('aws.servicesPage.autoInstall') }}
                    </RexButton>
                  </div>
                </template>
              </Card>
            </RexColumn>
          </component>
          <RexColumn :span="manualInstallServiceSlug ? 16 : 0">
            <RdsCreateEditForm
              type="create"
              @close="closeFormCards"
              @update-all-services="updateAllServices"
              v-if="manualInstallServiceSlug === 'rds'"
            />
            <LambdaCreateEditForm
              type="create"
              @close="closeFormCards"
              @update-all-services="updateAllServices"
              v-if="manualInstallServiceSlug === 'lambda'"
            />
            <ElastiCacheCreateEditForm
              type="create"
              @close="closeFormCards"
              @update-all-services="updateAllServices"
              v-if="manualInstallServiceSlug === 'elasticache'"
            />
            <IngressCreateEditForm
              type="create"
              @close="closeFormCards"
              @update-all-services="updateAllServices"
              v-if="manualInstallServiceSlug === 'elb'"
            />
          </RexColumn>
        </RexRow>
        <!-- container of all the cards component and the form component starts -->
      </div>

      <!-- available section header -->
    </div>
    <!-- available section ends -->
  </div>
</template>

<style lang="scss" scoped>
* {
  color: white;
}

.flex-between {
  @include flex-center-justify(space-between);
  gap: 0.3125rem;
}

.no-installed-services-container {
  width: 30rem;
  align-items: center;
  left: 35%;
  position: relative;
}

.no-install-text-description {
  text-align: center;
  color: $color-gray;
  font-size: 14px;
  font-weight: 400;
}

.flex-center {
  @include flex-center;
  gap: 0.3125rem;
}

.collapse-button {
  font-size: 1.5rem;
  font-weight: bolder !important;
}

.unstyled-button {
  width: fit-content;
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  cursor: pointer;
  font-weight: bold;
  padding: 0 0.3125rem;
  color: $color-gray;
}

.unstyled-button:disabled {
  cursor: not-allowed;
}

.collapse {
  gap: 0.75rem;
}

.card-wrapper {
  width: 33.3333%;
}

#auto-installrds {
  color: $color-brand-accent;
  font-weight: 600;
}

.installed-section,
.no-install-text {
  @include flex-column;
  font-weight: bold;
  font-size: large;
  gap: 0.3125rem;
  padding: 1.25rem 0;
}

.available-section {
  @include flex-column;
  gap: 0.3125rem;
  padding: 1.25rem 0;
}

.cards-container {
  max-height: 38.125rem;
  overflow-y: auto;
  width: 100%;
}

.card-footer {
  border-top: 0.0125rem solid $border-active;
}

.card-title {
  font-weight: bold;
  font-size: 1.5rem;
  font-family: RakutenSansUI-Bold !important;
}

.card-descritpion,
.card-title {
  text-align: justify;
}

.card-descritpion {
  font-weight: normal;
}

.custom-control-input:checked ~ .custom-control-label::before {
  color: #fff;
  border-color: #f59600 !important;
  background-color: #f59600 !important;
}

.instance-heartbeat {
  width: 0.875rem;
  height: 0.875rem;
  border-radius: 50%;
  margin-bottom: 0.375rem;
  margin-left: 0.3125rem;
}

.green {
  background-color: green;
}

.red {
  background-color: red;
}

.p-4 {
  padding: 0.5rem;
}
</style>
